/**
 * Application Constants
 *
 * Centralized constants to avoid magic strings and improve maintainability.
 */

// PA1 — the content status-transition machine is single-sourced in shared/publish/
// transitions.js (consumed by BOTH runtimes, exactly like shared/config/chains.js). The
// status-shape review-lane guard helpers below are thin re-exports of its predicates, so
// the frontend can NEVER drift from the backend's legal table. Role-based guards
// (canRoleReviewContent, canRoleApproveApplications) are NOT status-shape and stay local.
import { isUnderReview, isReviewPublishable } from '@shared/publish/transitions.js';

// ============================================================================
// Application Status
// ============================================================================

export const ApplicationStatus = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
} as const;

export type ApplicationStatusType = typeof ApplicationStatus[keyof typeof ApplicationStatus];

// ============================================================================
// Article Status
// ============================================================================

export const ArticleStatus = {
  DRAFT: 'draft',
  SUBMITTED: 'submitted',
  IN_REVIEW: 'in_review',
  NEEDS_REVISION: 'needs_revision',
  SCHEDULED: 'scheduled', // approved, waiting for scheduledPublishAt; the publish cron flips it to PUBLISHED
  PUBLISHED: 'published',
  REJECTED: 'rejected',
} as const;

export type ArticleStatusType = typeof ArticleStatus[keyof typeof ArticleStatus];

// ============================================================================
// Breaking News Status (mirrors ArticleStatus for unified workflow)
// ============================================================================

export const BreakingNewsStatus = {
  DRAFT: 'draft',
  SUBMITTED: 'submitted',
  IN_REVIEW: 'in_review',
  NEEDS_REVISION: 'needs_revision',
  PUBLISHED: 'published',
  REJECTED: 'rejected',
} as const;

export type BreakingNewsStatusType = typeof BreakingNewsStatus[keyof typeof BreakingNewsStatus];

// ============================================================================
// Content Status (unified type for both articles and breaking news)
// ============================================================================

export const ContentStatus = {
  DRAFT: 'draft',
  SUBMITTED: 'submitted',
  IN_REVIEW: 'in_review',
  NEEDS_REVISION: 'needs_revision',
  PUBLISHED: 'published',
  REJECTED: 'rejected',
} as const;

export type ContentStatusType = typeof ContentStatus[keyof typeof ContentStatus];

// ============================================================================
// Article Visibility (Public Access Control)
// ============================================================================

export const ArticleVisibility = {
  PUBLIC: 'public',       // Full access - appears in feeds, sitemaps, carousels
  PREVIEW: 'preview',     // URL access only - noindex, shows preview banner
  UNLISTED: 'unlisted',   // URL access only - noindex, no banner (for sharing)
  HIDDEN: 'hidden',       // No public access - only visible in admin
} as const;

export type ArticleVisibilityType = typeof ArticleVisibility[keyof typeof ArticleVisibility];

/**
 * Check if an article visibility allows public URL access
 */
export function isVisibilityAccessible(visibility: string | undefined): boolean {
  // Default to public if not set (backward compatibility)
  if (!visibility) return true;
  const accessibleVisibilities = [
    ArticleVisibility.PUBLIC,
    ArticleVisibility.PREVIEW,
    ArticleVisibility.UNLISTED,
  ] as const;
  return (accessibleVisibilities as readonly string[]).includes(visibility);
}

/**
 * Check if an article visibility should be indexed by search engines
 */
export function isVisibilityIndexable(visibility: string | undefined): boolean {
  // Default to public if not set (backward compatibility)
  if (!visibility) return true;
  return visibility === ArticleVisibility.PUBLIC;
}

// ============================================================================
// User Roles
// ============================================================================

export const UserRole = {
  // CONTRIBUTOR: the user-world default for NEW Twitter logins. Contributors submit
  // signals/answers; they have NO editorial/CMS/admin perms and NEVER land in /admin.
  // Sits BELOW writer in the hierarchy (see AuthContext.hasMinimumRole).
  CONTRIBUTOR: 'contributor',
  // MEMBER (WS-K L3): a contributor who has PROVEN ownership of an entity via the proof ladder
  // (claim → verify). A member's answer on an entity they OWN is asserted `human:member:<uid>`
  // (right-of-reply); everywhere else they behave exactly like a contributor. Still user-world:
  // member is NOT staff (isStaffRole(member)===false), never reaches /admin, and (THE COI LINE)
  // can never set a verdict. Sits ABOVE contributor, BELOW writer in the hierarchy.
  MEMBER: 'member',
  // WRITER: FROZEN — the community-writer path is parked. Keep the role in the enum so
  // existing writer docs keep working, but DO NOT assign 'writer' to new users.
  WRITER: 'writer',
  EDITOR: 'editor',
  ADMIN: 'admin',
  OWNER: 'owner',
  GUEST: 'guest',
} as const;

export type UserRoleType = typeof UserRole[keyof typeof UserRole];

// ============================================================================
// Activity Log Types
// ============================================================================

export const ActivityType = {
  APPLICATION_APPROVED: 'application_approved',
  APPLICATION_REJECTED: 'application_rejected',
  ARTICLE_APPROVED: 'article_approved',
  ARTICLE_REJECTED: 'article_rejected',
  ARTICLE_REVISIONS_REQUESTED: 'article_revision_requested',
} as const;

export type ActivityTypeType = typeof ActivityType[keyof typeof ActivityType];

// ============================================================================
// Activity Visibility
// ============================================================================

export const ActivityVisibility = {
  PUBLIC: 'public',
  ADMIN: 'admin',
  PRIVATE: 'private',
} as const;

export type ActivityVisibilityType = typeof ActivityVisibility[keyof typeof ActivityVisibility];

// ============================================================================
// Error Messages
// ============================================================================

export const ErrorMessages = {
  // Authentication Errors
  AUTH_MISSING_TOKEN: 'Unauthorized - Missing authentication token',
  AUTH_INVALID_TOKEN: 'Unauthorized - Invalid or expired token',
  AUTH_USER_NOT_FOUND: 'User not found',
  
  // Authorization Errors
  FORBIDDEN_ADMIN_ONLY: 'Forbidden - Only administrators can perform this action',
  FORBIDDEN_EDITOR_ONLY: 'Forbidden - Only editors, admins, and owners can perform this action',
  FORBIDDEN_APPROVE_APPS: 'Forbidden - Only administrators can approve applications',
  FORBIDDEN_REJECT_APPS: 'Forbidden - Only administrators can reject applications',
  FORBIDDEN_APPROVE_ARTICLES: 'Forbidden - Only editors, admins, and owners can approve articles',
  FORBIDDEN_REJECT_ARTICLES: 'Forbidden - Only editors, admins, and owners can reject articles',
  FORBIDDEN_REQUEST_REVISIONS: 'Forbidden - Only editors, admins, and owners can request revisions',
  
  // Validation Errors
  VALIDATION_MISSING_FIELDS: 'Missing required fields',
  VALIDATION_INVALID_STATUS: 'Invalid status for this operation',
  VALIDATION_FEEDBACK_REQUIRED: 'Feedback is required',
  VALIDATION_FEEDBACK_TOO_LONG: 'Feedback must be 2000 characters or less',
  VALIDATION_REJECTION_TOO_LONG: 'Rejection reason must be 500 characters or less',
  VALIDATION_ARTICLE_NO_TAGS: 'Article has no tags assigned',
  
  // Resource Errors
  RESOURCE_USER_NOT_FOUND: 'User not found',
  RESOURCE_ARTICLE_NOT_FOUND: 'Article not found',
  RESOURCE_APPLICATION_NOT_FOUND: 'Application not found',
  
  // Operation Errors
  OPERATION_ALREADY_PROCESSED: 'This item has already been processed',
  OPERATION_INVALID_STATE: 'Invalid state for this operation',
  OPERATION_FAILED: 'Operation failed',
} as const;

// ============================================================================
// Validation Limits
// ============================================================================

export const ValidationLimits = {
  FEEDBACK_MAX_LENGTH: 2000,
  REJECTION_REASON_MAX_LENGTH: 500,
  ACTIVITY_LOG_PREVIEW_LENGTH: 100,
} as const;

// ============================================================================
// Site Configuration
// ============================================================================

export const SiteConfig = {
  DEFAULT_URL: 'https://descout.xyz',
  DEFAULT_FROM_EMAIL: 'notifications@descout.xyz',
} as const;

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Check if an application status is pending
 */
export function isApplicationPending(status: string): boolean {
  return status === ApplicationStatus.PENDING;
}

/**
 * Check if content can be reviewed (submitted or in_review)
 * Works for both articles and breaking news.
 * PA1: single-sourced over shared/publish/transitions.js (`isUnderReview`).
 */
export function canReviewContent(status: string): boolean {
  return isUnderReview(status);
}

/**
 * Check if an article can be reviewed (submitted or in_review)
 * @deprecated Use canReviewContent instead
 */
export function canReviewArticle(status: string): boolean {
  return canReviewContent(status);
}

/**
 * Check if content can be published (submitted, in_review, or needs_revision)
 * Works for both articles and breaking news
 */
export function canPublishContent(status: string): boolean {
  // PA1: single-sourced over shared/publish/transitions.js (`isReviewPublishable`) —
  // the set {submitted, in_review, needs_revision} an editor can approve→published from.
  return isReviewPublishable(status);
}

/**
 * Check if an article can be published (submitted, in_review, or needs_revision)
 * @deprecated Use canPublishContent instead
 */
export function canPublishArticle(status: string): boolean {
  return canPublishContent(status);
}

/**
 * Check if content is under review (submitted or in_review)
 * Works for both articles and breaking news
 */
export function isContentUnderReview(status: string): boolean {
  return isUnderReview(status); // PA1: single-sourced over shared/publish/transitions.js
}

/**
 * Check if a user role can review content (articles/breaking news)
 */
export function canRoleReviewContent(role: string): boolean {
  const reviewerRoles = [UserRole.EDITOR, UserRole.ADMIN, UserRole.OWNER] as const;
  return (reviewerRoles as readonly string[]).includes(role);
}

/**
 * Check if a user role can review articles
 * @deprecated Use canRoleReviewContent instead
 */
export function canRoleReviewArticles(role: string): boolean {
  return canRoleReviewContent(role);
}

/**
 * Check if a user role can approve applications
 */
export function canRoleApproveApplications(role: string): boolean {
  const approverRoles = [UserRole.ADMIN, UserRole.OWNER] as const;
  return (approverRoles as readonly string[]).includes(role);
}

/**
 * Get the site URL (with environment variable support)
 */
export function getSiteUrl(): string {
  return process.env.NEXT_PUBLIC_SITE_URL || SiteConfig.DEFAULT_URL;
}
